// SPDX-License-Identifier: AGPL-3.0-or-later

import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import {mediaUrl} from '@app/features/messaging/utils/MessagingUrlUtils';
import tier2Url from '@app/media/images/boost/booster-02.webp';
import tier3Url from '@app/media/images/boost/booster-03.webp';
import tier4Url from '@app/media/images/boost/booster-04.webp';
import tier6Url from '@app/media/images/boost/booster-06.webp';
import tier7Url from '@app/media/images/boost/booster-07.webp';
import tier8Url from '@app/media/images/boost/booster-08.webp';
import tier1Url from '@app/media/images/boost/level-1.webp';
import tier5Url from '@app/media/images/boost/level-2.webp';
import tier9Url from '@app/media/images/boost/level-3.webp';

// Discord's steps: the first badge shows from the first day, the next ones at 2, 3, 6, 9, 12, 15, 18 and 24 months.
// The guild level badges are the same art as steps 1, 5 and 9.
export const STEP_MONTHS = [0, 2, 3, 6, 9, 12, 15, 18, 24];
const STEP_URLS = [tier1Url, tier2Url, tier3Url, tier4Url, tier5Url, tier6Url, tier7Url, tier8Url, tier9Url];

// Whole calendar months (UTC) from the start of the unbroken run of boosting.
export function boosterBadgeMonths(since: Date, now: Date): number {
	const months =
		(now.getUTCFullYear() - since.getUTCFullYear()) * 12 +
		now.getUTCMonth() -
		since.getUTCMonth() -
		(now.getUTCDate() < since.getUTCDate() ? 1 : 0);
	return Math.max(0, months);
}

export function boosterBadgeTier(months: number): number {
	return STEP_MONTHS.filter((step) => months >= step).length;
}

// The UTC day boosterBadgeMonths first reaches the next of the month steps: the start day of the step's month, or the
// 1st of the month after when that month is shorter. null on the last step.
export function nextMonthStepAt(since: Date, now: Date, steps: ReadonlyArray<number>): Date | null {
	const months = boosterBadgeMonths(since, now);
	const step = steps.find((candidate) => candidate > months);
	if (step === undefined) return null;
	const year = since.getUTCFullYear();
	const month = since.getUTCMonth() + step;
	const day = since.getUTCDate();
	const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
	return day <= daysInMonth ? new Date(Date.UTC(year, month, day)) : new Date(Date.UTC(year, month + 1, 1));
}

export function boosterNextTierAt(since: Date, now: Date): Date | null {
	return nextMonthStepAt(since, now, STEP_MONTHS);
}

// An image uploaded in the admin panel (hash from the discovery document) wins over the bundled one.
export function boosterBadgeUrl(tier: number): string {
	const icon = RuntimeConfig.boosterBadgeIcons[tier - 1];
	return icon ? mediaUrl(`branding/booster-badge-${tier}/${icon}.webp?size=128`) : STEP_URLS[tier - 1];
}

const LEVEL_TIERS: Record<number, number> = {1: 1, 2: 5, 3: 9};

export function boostLevelBadgeUrl(level: number): string | null {
	const tier = LEVEL_TIERS[level];
	return tier ? boosterBadgeUrl(tier) : null;
}
